import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, ArrowRight, Star, Clock, Target, Dumbbell, X, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResultsPage = () => {
  const { programId } = useParams();
  const [program, setProgram] = useState(null);
  const [allPrograms, setAllPrograms] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPricing, setShowPricing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [programRes, allProgramsRes, pricingRes] = await Promise.all([
          axios.get(`${API}/programs/${programId}`),
          axios.get(`${API}/programs`),
          axios.get(`${API}/pricing`)
        ]);
        setProgram(programRes.data);
        setAllPrograms(allProgramsRes.data.filter(p => p.id !== programId).slice(0, 3));
        setPricing(pricingRes.data);
      } catch (error) {
        console.error("Error fetching program:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [programId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="font-oswald text-2xl uppercase tracking-wider animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-oswald text-4xl uppercase tracking-tighter mb-4">PROGRAM NOT FOUND</h1>
          <Link to="/assessment" className="btn-primary">
            RETAKE ASSESSMENT
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24" data-testid="program-hero">
          <div className="absolute inset-0 z-0">
            <img 
              src={program.image}
              alt={program.name}
              className="w-full h-full object-cover opacity-20 img-grayscale"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/90 to-black"></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-mvision-gold text-black px-4 py-2 mb-6">
                <Star size={16} fill="black" strokeWidth={0} />
                <span className="font-oswald uppercase tracking-wider text-sm">{program.duration} PROGRAM</span>
              </div>
              <h1 
                className="font-oswald text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tighter mb-6 animate-fade-in"
                data-testid="program-name"
              >
                {program.name}
              </h1>
              <p className="font-manrope text-neutral-300 text-xl mb-8">
                {program.description}
              </p>
              
              {/* Quick Stats */}
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2">
                  <Clock size={20} className="text-mvision-gold" />
                  <span className="font-manrope text-neutral-400">{program.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Target size={20} className="text-mvision-gold" />
                  <span className="font-manrope text-neutral-400 capitalize">{program.level.replace(",", " / ")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Dumbbell size={20} className="text-mvision-gold" />
                  <span className="font-manrope text-neutral-400">{program.goals.length} Goals</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => setShowPricing(true)}
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="get-started-btn"
                >
                  GET STARTED NOW
                  <ArrowRight size={20} />
                </button>
                <Link to="/booking" data-testid="book-consultation-btn">
                  <button className="btn-secondary">
                    BOOK FREE CONSULTATION
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Full Description */}
        <section className="py-16 md:py-24 section-divider" data-testid="program-description">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase tracking-tighter mb-6">
                  PROGRAM OVERVIEW
                </h2>
                <div className="w-16 h-0.5 bg-mvision-gold mb-8"></div>
                <p className="font-manrope text-neutral-400 text-lg leading-relaxed mb-8">
                  {program.full_description}
                </p>
                
                {/* Goals */}
                <div className="mb-8">
                  <h3 className="font-oswald text-xl uppercase tracking-tighter mb-4">IDEAL FOR</h3>
                  <div className="flex flex-wrap gap-2">
                    {program.goals.map((goal, index) => (
                      <span 
                        key={index}
                        className="font-oswald text-sm uppercase tracking-wider bg-neutral-900 border border-mvision-gold/30 px-4 py-2 text-mvision-gold"
                      >
                        {goal.replace("_", " ")}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="bg-neutral-950 border border-mvision-gold/30 p-8">
                <h3 className="font-oswald text-2xl uppercase tracking-tighter mb-6 text-mvision-gold">WHAT'S INCLUDED</h3>
                <ul className="space-y-4">
                  {program.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-mvision-gold text-black flex items-center justify-center flex-shrink-0">
                        <Check size={18} />
                      </div>
                      <span className="font-manrope text-neutral-300 text-lg">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-8 pt-8 border-t border-neutral-800">
                  <p className="font-oswald text-sm uppercase tracking-wider text-neutral-500 mb-2">PROGRAM DURATION</p>
                  <p className="font-oswald text-3xl text-mvision-gold">{program.duration}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Progression Map */}
        <section className="py-16 md:py-24 bg-neutral-950 section-divider" data-testid="progression-map">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase tracking-tighter mb-4">
                YOUR TRANSFORMATION JOURNEY
              </h2>
              <div className="w-16 h-0.5 bg-mvision-gold mx-auto mb-6"></div>
              <p className="font-manrope text-neutral-400 text-lg max-w-2xl mx-auto">
                Here's how we'll systematically progress you through the program to achieve maximum results.
              </p>
            </div>

            <div className="relative">
              {/* Timeline Line */}
              <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-mvision-gold/30 -translate-x-1/2"></div>
              
              <div className="space-y-8 lg:space-y-0">
                {program.progression && program.progression.map((phase, index) => (
                  <div 
                    key={index}
                    className={`relative lg:grid lg:grid-cols-2 lg:gap-12 ${index % 2 === 0 ? '' : 'lg:direction-rtl'}`}
                    data-testid={`phase-${index}`}
                  >
                    {/* Timeline Dot */}
                    <div className="hidden lg:flex absolute left-1/2 top-8 -translate-x-1/2 w-12 h-12 bg-mvision-gold text-black items-center justify-center font-oswald font-bold z-10">
                      {index + 1}
                    </div>
                    
                    {/* Content */}
                    <div className={`${index % 2 === 0 ? 'lg:pr-16 lg:text-right' : 'lg:col-start-2 lg:pl-16'}`}>
                      <div className={`bg-black border border-neutral-800 p-6 lg:p-8 hover:border-mvision-gold transition-colors ${index % 2 === 0 ? '' : ''}`}>
                        <div className="flex lg:hidden items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-mvision-gold text-black flex items-center justify-center font-oswald font-bold">
                            {index + 1}
                          </div>
                          <span className="font-oswald text-sm uppercase tracking-wider text-mvision-gold">{phase.month}</span>
                        </div>
                        <span className="hidden lg:block font-oswald text-sm uppercase tracking-wider text-mvision-gold mb-2">{phase.month}</span>
                        <h3 className="font-oswald text-2xl uppercase tracking-tighter mb-2">{phase.title}</h3>
                        <p className="font-oswald text-sm uppercase tracking-wider text-white/60 mb-4">{phase.focus}</p>
                        <p className="font-manrope text-neutral-400">{phase.description}</p>
                      </div>
                    </div>
                    
                    {/* Spacer for alternating layout */}
                    {index % 2 === 0 && <div className="hidden lg:block"></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 section-divider" data-testid="cta-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="bg-gradient-to-r from-mvision-gold to-mvision-goldLight text-black p-8 md:p-12 text-center">
              <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase tracking-tighter mb-4">
                READY TO START YOUR TRANSFORMATION?
              </h2>
              <p className="font-manrope text-black/70 text-lg mb-8 max-w-2xl mx-auto">
                Join the {program.name} program today and take the first step toward achieving your fitness goals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => setShowPricing(true)}
                  className="bg-black text-white font-oswald uppercase tracking-wider px-8 py-4 hover:bg-neutral-800 transition-colors"
                  data-testid="cta-pricing-btn"
                >
                  VIEW PRICING & ENROLL
                </button>
                <Link to="/booking" data-testid="cta-booking-btn">
                  <button className="border-2 border-black text-black font-oswald uppercase tracking-wider px-8 py-4 hover:bg-black hover:text-white transition-colors">
                    BOOK CONSULTATION
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Other Programs */}
        {allPrograms.length > 0 && (
          <section className="py-16 md:py-24 bg-neutral-950 section-divider" data-testid="other-programs">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="text-center mb-12">
                <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase tracking-tighter mb-4">
                  EXPLORE OTHER PROGRAMS
                </h2>
                <div className="w-16 h-0.5 bg-mvision-gold mx-auto"></div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {allPrograms.map((prog) => (
                  <Link 
                    key={prog.id}
                    to={`/results/${prog.id}`}
                    className="card-hover bg-black p-0 overflow-hidden group block"
                    data-testid={`other-program-${prog.id}`}
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={prog.image}
                        alt={prog.name}
                        className="w-full h-full object-cover img-grayscale group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/40"></div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-oswald text-lg font-bold uppercase tracking-tighter mb-2">
                        {prog.name}
                      </h3>
                      <p className="font-manrope text-neutral-500 text-sm mb-4 line-clamp-2">
                        {prog.description}
                      </p>
                      <p className="font-oswald text-xs uppercase tracking-wider text-mvision-gold">
                        {prog.duration}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="text-center mt-10">
                <Link to="/programs" data-testid="view-all-programs-btn">
                  <button className="btn-secondary">
                    VIEW ALL PROGRAMS
                  </button>
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Pricing Modal */}
      {showPricing && (
        <PricingModal 
          pricing={pricing} 
          programName={program.name}
          onClose={() => setShowPricing(false)} 
        />
      )}

      <Footer />
    </div>
  );
};

const PricingModal = ({ pricing, programName, onClose }) => {
  const [loadingPackage, setLoadingPackage] = useState(null);

  const handlePayment = async (packageId) => {
    setLoadingPackage(packageId);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Failed to start checkout. Please try again.");
    }
    setLoadingPackage(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="relative bg-black border border-mvision-gold/30 max-w-5xl w-full max-h-[90vh] overflow-y-auto" data-testid="pricing-modal">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-neutral-400 hover:text-white transition-colors z-10"
          data-testid="close-modal-btn"
        >
          <X size={24} />
        </button>

        <div className="p-8 md:p-12">
          <div className="text-center mb-10">
            <h2 className="font-oswald text-3xl md:text-4xl font-bold uppercase tracking-tighter mb-4">
              CHOOSE YOUR PLAN
            </h2>
            <p className="font-manrope text-neutral-400">
              Select a package to start your <span className="text-mvision-gold">{programName}</span> journey
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {pricing.map((pkg) => (
              <div 
                key={pkg.id}
                className={`p-6 border ${pkg.popular ? 'border-mvision-gold bg-mvision-gold/5' : 'border-neutral-800'} transition-all hover:border-mvision-gold`}
                data-testid={`modal-pricing-${pkg.id}`}
              >
                {pkg.popular && (
                  <p className="font-oswald text-xs uppercase tracking-wider text-black bg-mvision-gold px-3 py-1 inline-block mb-4">
                    MOST POPULAR
                  </p>
                )}
                <h3 className="font-oswald text-2xl font-bold uppercase tracking-tighter mb-2">
                  {pkg.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="font-oswald text-4xl font-bold text-mvision-gold">${pkg.price}</span>
                  <span className="font-manrope text-neutral-500">/{pkg.duration}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={16} className="text-mvision-gold mt-1 flex-shrink-0" />
                      <span className="font-manrope text-neutral-400 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={() => handlePayment(pkg.id)}
                  disabled={loadingPackage === pkg.id}
                  className={`w-full py-3 font-oswald uppercase tracking-wider text-sm transition-all ${
                    pkg.popular 
                      ? 'bg-mvision-gold text-black hover:bg-mvision-goldLight' 
                      : 'border border-mvision-gold text-mvision-gold hover:bg-mvision-gold hover:text-black'
                  }`}
                  data-testid={`checkout-${pkg.id}`}
                >
                  {loadingPackage === pkg.id ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      PROCESSING...
                    </span>
                  ) : (
                    "SELECT PLAN"
                  )}
                </button>
              </div>
            ))}
          </div>

          <p className="text-center mt-8 text-neutral-500 text-sm">
            All plans include access to your personalized {programName} program
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
